/**
 * THE HEAD-AUTHORITY CONTRACT (C-356) — a source-level audit.
 *
 * WHY THIS EXISTS. One undefined contract produced two OPPOSITE production
 * failures on the same shared composable, measured a day apart:
 *
 *   KEPT           28 `applyHead({…})` callers OVERWROTE the baked head.
 *                  93 title/description divergences across 49 routes — 100% of
 *                  routes served one <title> to a non-JS AI crawler and a
 *                  different one to Google and every human (C-338 matrix,
 *                  fixed C-341: 51/51, zero divergent).
 *   boogie-babies  2 callers, 9 of 11 views that write NOTHING. Hard navigation
 *                  is correct; the first in-app click leaves the previous
 *                  route's title/description/canonical in the DOM (measured:
 *                  /pricing -> /parties still claims canonical /pricing).
 *   just-posh      17 raw `useHead({title, meta, link})` blocks that never touch
 *                  the composable at all — 27 divergences, the fleet's second
 *                  largest. A contract about `applyHead` alone would not see it.
 *
 * THE CONTRACT, in one line: `.dcs/seo.yaml` is the ONLY writer of the managed
 * head fields, and it must be re-asserted on EVERY route, on EVERY navigation.
 * Full text + reasoning: `.docs/plans/dynamic-site-resolution/README.md`
 * § "The head-authority contract (C-356)".
 *
 * WHAT THIS MODULE PROVES AND WHAT IT DOES NOT.
 *   - It proves there is exactly ONE WRITER in the source. That is authority.
 *   - It does NOT prove the writer told the truth. That is P1
 *     (`headHonesty.ts`), which compares the baked head to the rendered head on
 *     a real build. Neither replaces the other, and an exemption here buys no
 *     amnesty there.
 *
 * Deliberately framework-free and fs-free: it takes source strings, so the same
 * function runs in the package's own vitest fixtures, in a site's test script,
 * and in `cli/head-contract-audit.mjs` against a real repo. There is no second
 * implementation to drift.
 */

import type { UseSeoReturn } from '../types/seo'

// =============================================================================
// The compile-time half of the enforcement
// =============================================================================

/**
 * COMPILE-TIME PIN: `applyHead` must take ZERO parameters.
 *
 * `packages/cms/tsconfig.json` excludes `**\/*.test.ts`, so a `@ts-expect-error`
 * written in a test file is never evaluated by `pnpm type-check` — it would be a
 * comment that looks like a gate. This pin lives in a checked source file
 * instead: re-add a parameter to `UseSeoReturn['applyHead']` and the assignment
 * below stops compiling, which reds `type-check` in this package and, through
 * the published `.d.ts`, in every site that runs one.
 */
export type ApplyHeadTakesNoArguments = Parameters<UseSeoReturn['applyHead']> extends []
  ? true
  : false

export const APPLY_HEAD_TAKES_NO_ARGUMENTS: ApplyHeadTakesNoArguments = true

// =============================================================================
// Types
// =============================================================================

/** Codes that FAIL the contract. */
export type HeadContractViolationCode =
  /** `applyHead({…})` — a second writer for a managed field. Never exemptible. */
  | 'override-argument'
  /** `useSEO('x')` where `.dcs/seo.yaml` has no `pages.x.title`. Never exemptible. */
  | 'undeclared-slug'
  /** `useHead` / `useSeoMeta` / `document.title =` — a writer outside the factory. */
  | 'foreign-head-writer'
  /** An exemption whose file no longer violates anything (anti-rot). */
  | 'stale-exemption'
  /** An exemption with no real reason recorded. */
  | 'empty-exemption-reason'
  /** An exemption that names a host but cannot be checked against it (C-420). */
  | 'unprobed-exemption'
  /** A live probe contradicts what the exemption asserts about its host (C-420). */
  | 'exemption-claim-refuted'
  /** The probe of an exemption's host did not complete — never a pass (C-420). */
  | 'exemption-probe-failed'
  /** The audit scanned no files — a pass that checked nothing is not a pass. */
  | 'scan-empty'
  /** No `.dcs/seo.yaml` was supplied, so authority cannot be established. */
  | 'no-seo-config'

/** Codes that are REPORTED but do not fail. */
export type HeadContractWarningCode =
  /** A view that never re-asserts the head; SPA navigation to it is stale. */
  | 'uncovered-route'
  /** `useSEO(`blog-${slug}`)` — legal, but only P1 can judge it. */
  | 'dynamic-slug'

export interface HeadContractSourceFile {
  /** Path as the caller wants it reported (and as exemptions key on). */
  path: string
  source: string
}

/** The slice of `.dcs/seo.yaml` this audit needs. */
export interface HeadContractSeoConfig {
  global?: { titleTemplate?: string; defaultTitle?: string } & Record<string, unknown>
  pages?: Record<string, { title?: string } & Record<string, unknown>>
}

// =============================================================================
// Exemptions and their LIVE claims (C-420)
// =============================================================================

/**
 * WHY EXEMPTIONS CARRY LIVE CLAIMS.
 *
 * An exemption used to be a sentence. A sentence is an assertion, and an
 * assertion nobody re-measures rots into a false premise that the audit then
 * defends: the measured case declared a host as a "noindex cross-domain
 * handoff", and every part of that was false in production — the host served
 * `index, follow`, a SELF-referential canonical, a 200 `robots.txt` that allows
 * every crawler, a sitemap and a full independent site. The audit was green
 * BECAUSE of the exemption, so the green was the bug.
 *
 * The discriminator is the exempted host's real response, not the bundle hash.
 * So an exemption that NAMES a host must now say what it expects that host to
 * do, and the caller must supply what the host actually did. A named host with
 * nothing checkable, or with nothing measured, is a VIOLATION — not a skip.
 *
 * This module still never touches the network: it takes observations. The
 * fetching lives in the runner (`cli/head-contract-audit.mjs`), the judging
 * lives here, and `parseHostProbeHtml` below is the shared, testable parser
 * between them so there is no second implementation to drift.
 */
export interface HeadContractExemptionExpectation {
  /** HTTP status, measured WITHOUT following redirects (a 301 is not a 200). */
  status?: number
  /** Substring that must appear in `<meta name="robots">` (case-insensitive). */
  robots?: string
  /** Hostname of `<link rel="canonical">` — the self-vs-cross-domain question. */
  canonicalHost?: string
  /** Substring that must appear in `<title>` (case-insensitive). */
  titleContains?: string
}

export interface HeadContractExemptionRecord {
  /** Why this file cannot route its head through the factory. */
  reason: string
  /**
   * `host -> claims`. EVERY host the reason names must appear here, and every
   * entry must carry at least one claim, or the exemption is unprobed.
   */
  hosts?: Record<string, HeadContractExemptionExpectation>
}

/** A bare string is still a legal exemption — it just may not name a host. */
export type HeadContractExemptionValue = string | HeadContractExemptionRecord

/** What a caller actually observed at a host. Never produced by this module. */
export interface HeadContractHostProbe {
  host: string
  /** `false` = the probe did not complete. A failed probe is never a pass. */
  ok: boolean
  error?: string
  status?: number
  robots?: string | null
  canonical?: string | null
  title?: string | null
  observedAt?: string
}

export type HeadContractExemptionVerdict =
  /** Every claim agreed with the live response. */
  | 'live-true'
  /** The live response contradicts at least one claim. */
  | 'refuted'
  /** Names a host but nothing checkable reached the judge. */
  | 'unprobed'
  /** A probe was attempted and did not complete. */
  | 'probe-failed'
  /** Asserts nothing about any host — nothing to probe. */
  | 'no-host-claim'

export interface HeadContractExemptionCheck {
  host: string
  claim: keyof HeadContractExemptionExpectation
  expected: string
  actual: string
  pass: boolean
}

export interface HeadContractExemptionAudit {
  file: string
  reason: string
  /** Hosts the PROSE names — the claim the author made whether or not they meant to. */
  hostsNamedInReason: string[]
  /** Hosts the author declared machine-checkable claims for. */
  hostsDeclared: string[]
  checks: HeadContractExemptionCheck[]
  verdict: HeadContractExemptionVerdict
}

export interface HeadContractInput {
  files: HeadContractSourceFile[]
  seo: HeadContractSeoConfig | undefined
  /**
   * `path -> reason | record`. Declares a KNOWN foreign head writer so it stops
   * being a surprise. It is a declaration of debt, not a licence: P1 still
   * measures whether that writer agrees with the baked head, a stale entry is
   * itself a violation, and (C-420) any host the declaration names is measured
   * against its live response.
   */
  exemptions?: Record<string, HeadContractExemptionValue>
  /**
   * `host -> what that host actually did`, supplied by the caller. An exemption
   * that names a host and finds no observation here FAILS: "not measured" and
   * "measured fine" must never render the same.
   */
  hostProbes?: Record<string, HeadContractHostProbe>
}

export interface HeadContractViolation {
  code: HeadContractViolationCode
  file: string
  line: number
  detail: string
}

export interface HeadContractWarning {
  code: HeadContractWarningCode
  file: string
  detail: string
}

export interface ForeignWriterSite {
  file: string
  line: number
  mechanism: 'useHead' | 'useSeoMeta' | 'document.title'
  exempted: boolean
  reason: string | null
}

export interface HeadContractReport {
  /** Execution counts FIRST — a rail that did not run must not read as green. */
  scanned: number
  applyHeadCalls: number
  argumentFreeCalls: number
  /** Slugs written as string literals — checked against `.dcs/seo.yaml`. */
  useSeoSlugs: string[]
  /** Slugs composed at runtime — legal, UNCHECKED here, judged by P1. */
  dynamicSlugs: string[]
  foreignWriters: ForeignWriterSite[]
  /** `useHead({script})` blocks that ADD JSON-LD without restating a managed field. */
  additiveHeadCalls: number
  /**
   * EVERY declared exemption with its live verdict — dumped whether or not it
   * found anything, so "0 refuted" is a measurement of a listed set and not an
   * empty listing wearing a zero.
   */
  exemptionAudit: HeadContractExemptionAudit[]
  coverage: {
    /** Files scanned (the caller decides what counts as a routed view). */
    views: number
    /** Files that re-assert the head in some way. */
    asserting: number
    /** Files that write nothing — SPA navigation to them is stale. */
    silent: string[]
  }
  violations: HeadContractViolation[]
  warnings: HeadContractWarning[]
  ok: boolean
}

export type HeadContractMode = 'error' | 'warn' | 'off'

export class HeadContractError extends Error {
  readonly report: HeadContractReport
  constructor(message: string, report: HeadContractReport) {
    super(message)
    this.name = 'HeadContractError'
    this.report = report
  }
}

/** Minimum characters an exemption reason must carry to count as a reason. */
export const MIN_EXEMPTION_REASON_LENGTH = 12

// =============================================================================
// Source scanning
// =============================================================================

/**
 * Blank out COMMENTS ONLY — line, block and HTML — tracking string/template
 * literals just well enough not to mistake a `https://…` inside a string for a
 * line comment.
 *
 * Replacement preserves LENGTH and NEWLINES (every removed character becomes a
 * space, newlines survive), so every offset and line number computed on the
 * result is still valid against the original file.
 *
 * WHY COMMENTS ONLY. Two hazards pull in opposite directions and the asymmetry
 * decides it:
 *   - boogie-babies' PrivacyView.vue carries a docblock reading "applyHead takes
 *     NO arguments". Scanning raw source reports it. So comments MUST go.
 *   - Blanking string BODIES as well would let a regex literal containing a
 *     quote (`/['"]/`) swallow the following lines and HIDE a real violation.
 *     A gate that silently misses is worse than one that occasionally shouts:
 *     a false positive is loud and fixed in a minute; a false negative is
 *     exactly the class this whole contract exists to kill.
 * So string bodies are left intact, and an `applyHead(` written inside a string
 * literal is deliberately reported.
 */
export function blankComments(source: string): string {
  const out = source.split('')
  const n = source.length
  let i = 0

  const blank = (from: number, to: number) => {
    for (let k = from; k < to && k < n; k += 1) {
      if (out[k] !== '\n') out[k] = ' '
    }
  }

  while (i < n) {
    const c = source[i]
    const next = source[i + 1]

    // HTML comment (Vue SFC <template> / index.html)
    if (c === '<' && source.startsWith('<!--', i)) {
      const end = source.indexOf('-->', i + 4)
      const stop = end === -1 ? n : end + 3
      blank(i, stop)
      i = stop
      continue
    }

    // Block comment
    if (c === '/' && next === '*') {
      const end = source.indexOf('*/', i + 2)
      const stop = end === -1 ? n : end + 2
      blank(i, stop)
      i = stop
      continue
    }

    // Line comment
    if (c === '/' && next === '/') {
      let end = source.indexOf('\n', i + 2)
      if (end === -1) end = n
      blank(i, end)
      i = end
      continue
    }

    // String / template literal — SKIPPED OVER, never blanked. Tracking it here
    // is only so a `//` inside a URL is not read as a line comment.
    if (c === '"' || c === "'" || c === '`') {
      const quote = c
      let k = i + 1
      while (k < n) {
        if (source[k] === '\\') {
          k += 2
          continue
        }
        if (source[k] === quote) break
        // An unterminated single/double quote must not swallow the file.
        if (quote !== '`' && source[k] === '\n') break
        k += 1
      }
      i = Math.min(k + 1, n)
      continue
    }

    i += 1
  }

  return out.join('')
}

function lineOf(source: string, index: number): number {
  let line = 1
  for (let i = 0; i < index && i < source.length; i += 1) {
    if (source[i] === '\n') line += 1
  }
  return line
}

/** Call-shaped `applyHead(` — never the `const { applyHead } = …` destructure. */
const APPLY_HEAD_CALL = /(?<![.\w$])applyHead\s*\(/g
/** `useSEO(` — the first argument is then inspected by hand (it may not be a literal). */
const USE_SEO_CALL = /(?<![.\w$])useSEO\s*\(\s*/g

/**
 * Read `useSEO`'s first argument.
 *
 * A dynamic route (`/blog/:slug`, `/topics/:topic`) cannot name a static slug —
 * it composes the per-route key the EMITTER uses. That is legal under the
 * contract and it is NOT statically checkable, so it is reported as dynamic and
 * handed to P1, which renders each real route and compares the two heads. What
 * is NOT legal is a dynamic route hand-building its own title string; that shows
 * up here as an override or a foreign writer like any other.
 */
function readUseSeoArgument(
  source: string,
  from: number
): { kind: 'static'; slug: string } | { kind: 'dynamic'; expression: string } {
  const opener = source[from]
  if (opener === "'" || opener === '"') {
    const end = source.indexOf(opener, from + 1)
    if (end === -1) return { kind: 'dynamic', expression: source.slice(from, from + 40) }
    return { kind: 'static', slug: source.slice(from + 1, end).trim() }
  }
  if (opener === '`') {
    const end = source.indexOf('`', from + 1)
    if (end === -1) return { kind: 'dynamic', expression: source.slice(from, from + 40) }
    const body = source.slice(from + 1, end)
    return body.includes('${')
      ? { kind: 'dynamic', expression: body }
      : { kind: 'static', slug: body.trim() }
  }
  const end = source.indexOf(')', from)
  return { kind: 'dynamic', expression: source.slice(from, end === -1 ? from + 40 : end).trim() }
}

const FOREIGN_WRITERS: Array<{ re: RegExp; mechanism: ForeignWriterSite['mechanism'] }> = [
  { re: /(?<![.\w$])useHead\s*\(/g, mechanism: 'useHead' },
  { re: /(?<![.\w$])useSeoMeta\s*\(/g, mechanism: 'useSeoMeta' },
  { re: /document\s*\.\s*title\s*=(?!=)/g, mechanism: 'document.title' },
]

/** Head keys whose value the contract reserves for `.dcs/seo.yaml`. */
const MANAGED_HEAD_KEYS = /(^|[{,\s([])(title|titleTemplate|meta|link|templateParams)\s*:/
/** The one head key a page may legitimately add on its own. */
const ADDITIVE_HEAD_KEYS = /(^|[{,\s([])script\s*:/
/** Cap so a runaway scan cannot walk the whole file. */
const ARG_SCAN_LIMIT = 6000

/**
 * Is this `useHead(...)` call ADDITIVE-ONLY?
 *
 * A page may legitimately add JSON-LD that `seo.yaml` cannot express per route —
 * a per-post `BlogPosting`, for instance. `useHead({ script: [...] })` that sets
 * no `title`/`meta`/`link` adds a tag; it does not restate a managed one, and
 * treating it as a violation would push every blog template into the exemption
 * list. An allow-list padded with legal entries is an allow-list nobody reads,
 * so the discrimination belongs here, not in each site's exemption file.
 *
 * Conservative by construction: anything it cannot read as additive-only — a
 * spread, a variable, an unbalanced or over-long argument — is reported.
 */
function isAdditiveOnlyHeadCall(code: string, argStart: number): boolean {
  let depth = 0
  let i = argStart
  const end = Math.min(code.length, argStart + ARG_SCAN_LIMIT)
  for (; i < end; i += 1) {
    const c = code[i]
    if (c === '(' || c === '{' || c === '[') depth += 1
    else if (c === ')' || c === '}' || c === ']') {
      depth -= 1
      if (depth === 0) break
    }
  }
  if (depth !== 0) return false // unbalanced or truncated — do not guess
  const arg = code.slice(argStart, i + 1)
  if (arg.includes('...')) return false // a spread can carry anything
  return ADDITIVE_HEAD_KEYS.test(arg) && !MANAGED_HEAD_KEYS.test(arg)
}

// =============================================================================
// Exemption host claims — the live half (C-420)
// =============================================================================

/**
 * A hostname written in prose. The TLD suffix list is deliberate: a bare
 * `\w+(\.\w+)+` also matches `.dcs/seo.yaml`, `HomeView.vue` and `useSEO().
 * applyHead`, which would turn every exemption into a false host claim.
 */
const HOSTNAME_IN_TEXT =
  /\b(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|dev|co|us|ai|app|site|xyz)\b/gi

function normalizeHost(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/\.$/, '')
}

/** A bare string exemption is a record with no host claims. */
export function normalizeExemption(value: HeadContractExemptionValue): HeadContractExemptionRecord {
  if (typeof value === 'string') return { reason: value }
  return { reason: value?.reason ?? '', hosts: value?.hosts }
}

/** Every host the prose names, deduped and normalised. */
export function hostsNamedIn(reason: string): string[] {
  const found = reason.match(HOSTNAME_IN_TEXT) ?? []
  const out: string[] = []
  for (const raw of found) {
    const host = normalizeHost(raw)
    if (!out.includes(host)) out.push(host)
  }
  return out
}

/**
 * Pull the three head fields an exemption can make a claim about out of a live
 * response body. Shared by the runner and by this module's own tests so the
 * thing that fetches and the thing that judges cannot disagree about what a
 * `robots` meta is.
 */
export function parseHostProbeHtml(html: string): {
  robots: string | null
  canonical: string | null
  title: string | null
} {
  const robots =
    /<meta[^>]+name\s*=\s*["']robots["'][^>]*>/i
      .exec(html)?.[0]
      ?.match(/content\s*=\s*["']([^"']*)["']/i)?.[1]
      ?.trim() ?? null
  const canonical =
    /<link[^>]+rel\s*=\s*["']canonical["'][^>]*>/i
      .exec(html)?.[0]
      ?.match(/href\s*=\s*["']([^"']*)["']/i)?.[1]
      ?.trim() ?? null
  const title = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html)?.[1]?.trim() ?? null
  return { robots, canonical, title }
}

function hostOfUrl(url: string): string {
  const match = /^(?:https?:)?\/\/([^/?#]+)/i.exec(url.trim())
  if (match) return normalizeHost(match[1])
  return normalizeHost(url)
}

function claimChecks(
  host: string,
  expect: HeadContractExemptionExpectation,
  probe: HeadContractHostProbe
): HeadContractExemptionCheck[] {
  const checks: HeadContractExemptionCheck[] = []
  if (typeof expect.status === 'number') {
    checks.push({
      host,
      claim: 'status',
      expected: String(expect.status),
      actual: probe.status === undefined ? '(none)' : String(probe.status),
      pass: probe.status === expect.status,
    })
  }
  if (typeof expect.robots === 'string') {
    const actual = probe.robots ?? '(absent)'
    checks.push({
      host,
      claim: 'robots',
      expected: `contains "${expect.robots}"`,
      actual,
      pass: actual.toLowerCase().includes(expect.robots.toLowerCase()),
    })
  }
  if (typeof expect.canonicalHost === 'string') {
    const actual = probe.canonical ? hostOfUrl(probe.canonical) : '(absent)'
    checks.push({
      host,
      claim: 'canonicalHost',
      expected: normalizeHost(expect.canonicalHost),
      actual,
      pass: actual === normalizeHost(expect.canonicalHost),
    })
  }
  if (typeof expect.titleContains === 'string') {
    const actual = probe.title ?? '(absent)'
    checks.push({
      host,
      claim: 'titleContains',
      expected: `contains "${expect.titleContains}"`,
      actual,
      pass: actual.toLowerCase().includes(expect.titleContains.toLowerCase()),
    })
  }
  return checks
}

/**
 * Judge every exemption against the live behaviour of the host it names.
 *
 * Four ways to fail and only one way to pass, because the failure this exists
 * to catch is a green:
 *   - the prose names a host that carries no machine-checkable claim
 *   - a declared host asserts nothing (a claim block with no claims)
 *   - a declared host has no observation (nothing was measured)
 *   - an observation disagrees with a claim, or the probe itself did not run
 * Pure: it fetches nothing and reads no files.
 */
export function auditExemptionHosts(
  exemptions: Record<string, HeadContractExemptionValue>,
  hostProbes: Record<string, HeadContractHostProbe>
): { audits: HeadContractExemptionAudit[]; violations: HeadContractViolation[] } {
  const audits: HeadContractExemptionAudit[] = []
  const violations: HeadContractViolation[] = []

  // WORST WINS, and "a claim was contradicted" is worse than "a claim was
  // missing": a multi-host exemption with one dead premise must summarise as
  // REFUTED, or the headline count disagrees with the violations underneath it.
  const RANK: Record<HeadContractExemptionVerdict, number> = {
    refuted: 4,
    'probe-failed': 3,
    unprobed: 2,
    'live-true': 1,
    'no-host-claim': 0,
  }

  for (const file of Object.keys(exemptions)) {
    const record = normalizeExemption(exemptions[file])
    const named = hostsNamedIn(record.reason)
    const declaredMap = record.hosts ?? {}
    const declared = Object.keys(declaredMap).map(normalizeHost)
    const checks: HeadContractExemptionCheck[] = []
    let verdict: HeadContractExemptionVerdict = 'no-host-claim'
    const escalate = (next: HeadContractExemptionVerdict) => {
      if (RANK[next] > RANK[verdict]) verdict = next
    }

    const undeclared = named.filter((h) => !declared.includes(h))
    if (undeclared.length > 0) {
      escalate('unprobed')
      violations.push({
        code: 'unprobed-exemption',
        file,
        line: 0,
        detail:
          `This exemption's reason names ${undeclared.join(', ')} but declares no claim about ` +
          `it, so nothing checks whether the host still behaves the way the reason says. ` +
          `Add "hosts": { "${undeclared[0]}": { … } } with at least one of status / robots / ` +
          `canonicalHost / titleContains. An exemption that names a host and is never ` +
          `measured against it is an assertion, not a finding.`,
      })
    }

    for (const rawHost of Object.keys(declaredMap)) {
      const host = normalizeHost(rawHost)
      const expect = declaredMap[rawHost] ?? {}
      const claimCount = (
        ['status', 'robots', 'canonicalHost', 'titleContains'] as const
      ).filter((k) => expect[k] !== undefined).length

      if (claimCount === 0) {
        escalate('unprobed')
        violations.push({
          code: 'unprobed-exemption',
          file,
          line: 0,
          detail:
            `The exemption declares ${host} but asserts nothing about it. A host entry with ` +
            `no claim is probed into a guaranteed pass — the vacuous green this rail exists ` +
            `to refuse. State what the host must do: status / robots / canonicalHost / ` +
            `titleContains.`,
        })
        continue
      }

      const probe = hostProbes[host]
      if (!probe) {
        escalate('unprobed')
        violations.push({
          code: 'unprobed-exemption',
          file,
          line: 0,
          detail:
            `The exemption claims ${claimCount} thing(s) about ${host} and NO observation of ` +
            `that host reached the audit. "Not measured" must never render as "measured ` +
            `fine": run the audit with host probing enabled, or delete the claim.`,
        })
        continue
      }

      if (!probe.ok) {
        escalate('probe-failed')
        violations.push({
          code: 'exemption-probe-failed',
          file,
          line: 0,
          detail:
            `The probe of ${host} did not complete (${probe.error ?? 'no error recorded'}), so ` +
            `this exemption's premise is UNVERIFIED. A probe that could not run is a failure, ` +
            `not a skip — the alternative is an allow-list that goes green whenever the ` +
            `network does.`,
        })
        continue
      }

      const hostChecks = claimChecks(host, expect, probe)
      checks.push(...hostChecks)
      for (const check of hostChecks.filter((c) => !c.pass)) {
        escalate('refuted')
        violations.push({
          code: 'exemption-claim-refuted',
          file,
          line: 0,
          detail:
            `DEAD PREMISE. The exemption asserts ${check.host} ${check.claim} ` +
            `${check.expected}; the live host answered "${check.actual}". The exemption is ` +
            `why this file passes, so the pass rests on something production stopped doing. ` +
            `Re-word it to what is true, or delete it and the writer it protects.`,
        })
      }
      if (hostChecks.length > 0 && hostChecks.every((c) => c.pass)) escalate('live-true')
    }

    audits.push({
      file,
      reason: record.reason,
      hostsNamedInReason: named,
      hostsDeclared: declared,
      checks,
      verdict,
    })
  }

  return { audits, violations }
}

// =============================================================================
// The audit
// =============================================================================

/**
 * Audit a set of source files against the head-authority contract.
 *
 * Site-agnostic by construction: there is no site name, no path convention and
 * no per-shape branch anywhere in this function. The three measured shapes
 * (KEPT / boogie-babies / bryans) go through the identical call.
 */
export function auditHeadContract(input: HeadContractInput): HeadContractReport {
  const files = input.files ?? []
  const exemptions = input.exemptions ?? {}
  const violations: HeadContractViolation[] = []
  const warnings: HeadContractWarning[] = []
  const foreignWriters: ForeignWriterSite[] = []
  const useSeoSlugs: string[] = []
  const dynamicSlugs: string[] = []
  const silent: string[] = []

  let applyHeadCalls = 0
  let additiveHeadCalls = 0
  let argumentFreeCalls = 0
  let asserting = 0

  for (const { path, source } of files) {
    const code = blankComments(source)
    let fileAsserts = false

    // ── V1 — applyHead must take no arguments ───────────────────────────────
    APPLY_HEAD_CALL.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = APPLY_HEAD_CALL.exec(code)) !== null) {
      applyHeadCalls += 1
      fileAsserts = true
      const rest = code.slice(m.index + m[0].length)
      if (/^\s*\)/.test(rest)) {
        argumentFreeCalls += 1
      } else {
        const preview = source
          .slice(m.index, m.index + 140)
          .replace(/\s+/g, ' ')
          .trim()
        violations.push({
          code: 'override-argument',
          file: path,
          line: lineOf(source, m.index),
          detail:
            `applyHead() must take no arguments — .dcs/seo.yaml is the only writer of ` +
            `title/description/keywords/canonical/OG/Twitter/JSON-LD. Move the value there ` +
            `and delete the argument. Found: ${preview}…`,
        })
      }
    }

    // ── V3 — every STATIC useSEO slug must resolve to a declared page ───────
    USE_SEO_CALL.lastIndex = 0
    while ((m = USE_SEO_CALL.exec(code)) !== null) {
      fileAsserts = true
      const arg = readUseSeoArgument(source, m.index + m[0].length)
      if (arg.kind === 'dynamic') {
        if (!dynamicSlugs.includes(arg.expression)) dynamicSlugs.push(arg.expression)
        warnings.push({
          code: 'dynamic-slug',
          file: path,
          detail:
            `useSEO(${arg.expression.slice(0, 60)}) resolves its page key at runtime, so this ` +
            `audit cannot check it against .dcs/seo.yaml. That is legal — a dynamic route must ` +
            `resolve the SAME per-route key the emitter uses — but it is UNCHECKED HERE. P1 ` +
            `(baked vs rendered, on a real build of every route) is the rail that judges it.`,
        })
        continue
      }
      const slug = arg.slug
      // A slug seen in an earlier file has already been judged; reporting it
      // again would multiply one seo.yaml gap into many identical findings.
      if (!slug || useSeoSlugs.includes(slug)) continue
      useSeoSlugs.push(slug)
      const page = input.seo?.pages?.[slug]
      if (!page || typeof page.title !== 'string' || page.title.trim() === '') {
        violations.push({
          code: 'undeclared-slug',
          file: path,
          line: lineOf(source, m.index),
          detail:
            `useSEO('${slug}') has no pages.${slug}.title in .dcs/seo.yaml. At runtime it ` +
            `collapses to global.defaultTitle while the build-time emitter falls back to the ` +
            `pages.yaml route title — the same baked-vs-rendered divergence wearing a ` +
            `different hat. Declare the entry.`,
        })
      }
    }

    // ── V2 — no writer outside the factory ──────────────────────────────────
    for (const { re, mechanism } of FOREIGN_WRITERS) {
      re.lastIndex = 0
      while ((m = re.exec(code)) !== null) {
        fileAsserts = true

        // Additive JSON-LD is not a second writer — see isAdditiveOnlyHeadCall.
        if (mechanism === 'useHead' && isAdditiveOnlyHeadCall(code, m.index + m[0].length - 1)) {
          additiveHeadCalls += 1
          continue
        }

        const reason = Object.prototype.hasOwnProperty.call(exemptions, path)
          ? normalizeExemption(exemptions[path]).reason
          : null
        const exempted = typeof reason === 'string' && reason.trim().length >= MIN_EXEMPTION_REASON_LENGTH
        foreignWriters.push({
          file: path,
          line: lineOf(source, m.index),
          mechanism,
          exempted,
          reason: reason ?? null,
        })
        if (exempted) continue
        if (reason !== null) {
          violations.push({
            code: 'empty-exemption-reason',
            file: path,
            line: lineOf(source, m.index),
            detail:
              `An exemption for ${path} exists but records no reason (needs >= ` +
              `${MIN_EXEMPTION_REASON_LENGTH} characters). An undocumented exemption is ` +
              `indistinguishable from an accident.`,
          })
          continue
        }
        violations.push({
          code: 'foreign-head-writer',
          file: path,
          line: lineOf(source, m.index),
          detail:
            `${mechanism} writes the head outside the .dcs/seo.yaml factory. Route the value ` +
            `through seo.yaml + useSEO().applyHead(), or declare this file as an exemption ` +
            `with the reason it genuinely cannot. Declaring it does NOT excuse it from P1: ` +
            `the baked head and the rendered head must still agree.`,
        })
      }
    }

    if (fileAsserts) asserting += 1
    else silent.push(path)
  }

  // ── V4 (warn) — a view that never re-asserts is stale after the first click ─
  for (const file of silent) {
    warnings.push({
      code: 'uncovered-route',
      file,
      detail:
        `This view never re-asserts the head, so an in-app navigation to it leaves the ` +
        `PREVIOUS route's title/description/canonical in the DOM. The baked head is correct ` +
        `exactly once — on the document that was fetched. Reported, not enforced: the fix is ` +
        `a router-level re-assert in the platform, not eleven views remembering.`,
    })
  }

  // ── Exemption hygiene — anti-rot (source) AND anti-dead-premise (live) ──
  // The stale check asks "does this file still violate?"; the host audit asks
  // "does the host this exemption NAMES still behave the way it claims?". The
  // measured failure passed the first and would have failed the second.
  const hostAudit = auditExemptionHosts(exemptions, input.hostProbes ?? {})
  violations.push(...hostAudit.violations)

  const violatingFiles = new Set(foreignWriters.map((f) => f.file))
  for (const path of Object.keys(exemptions)) {
    if (!violatingFiles.has(path)) {
      violations.push({
        code: 'stale-exemption',
        file: path,
        line: 0,
        detail:
          `An exemption is declared for ${path} but it writes no head outside the factory. ` +
          `Delete the exemption — a rotting allow-list is how the next real one gets waved ` +
          `through.`,
      })
    }
  }

  // ── Execution assertions — a check that checked nothing is a FAILURE ─────
  if (files.length === 0) {
    violations.push({
      code: 'scan-empty',
      file: '(none)',
      line: 0,
      detail:
        'The head-contract audit SCANNED NOTHING. Zero files reached it, so it proved ' +
        'nothing about this site. The file walk is broken, not the codebase.',
    })
  }
  if (!input.seo || !input.seo.pages || Object.keys(input.seo.pages).length === 0) {
    violations.push({
      code: 'no-seo-config',
      file: '.dcs/seo.yaml',
      line: 0,
      detail:
        'No .dcs/seo.yaml pages were supplied. Authority cannot be established without the ' +
        'authority file, so every slug check silently passes — the vacuous-green shape this ' +
        'audit exists to refuse.',
    })
  }

  return {
    scanned: files.length,
    applyHeadCalls,
    argumentFreeCalls,
    useSeoSlugs,
    dynamicSlugs,
    foreignWriters,
    additiveHeadCalls,
    exemptionAudit: hostAudit.audits,
    coverage: { views: files.length, asserting, silent },
    violations,
    warnings,
    ok: violations.length === 0,
  }
}

/**
 * Dump the exemption set with a per-entry live verdict.
 *
 * Printed on EVERY run, including the empty one. "0 refuted" out of an unlisted
 * set is indistinguishable from a rail that inspected nothing, so the set is
 * always enumerated and the empty case says so in words.
 */
export function formatExemptionAudit(report: HeadContractReport): string[] {
  const audits = report.exemptionAudit ?? []
  const lines: string[] = []
  const count = (v: HeadContractExemptionVerdict) => audits.filter((a) => a.verdict === v).length

  if (audits.length === 0) {
    lines.push(
      `[head-contract] exemptions: 0 declared — the allow-list is EMPTY, not unchecked ` +
        `(${report.scanned} file(s) scanned, ${report.foreignWriters.length} foreign head ` +
        `writer(s) found, so there was nothing to declare).`
    )
    return lines
  }

  lines.push(
    `[head-contract] exemptions: ${audits.length} declared — ${count('live-true')} live-true, ` +
      `${count('refuted')} REFUTED, ${count('unprobed')} unprobed, ` +
      `${count('probe-failed')} probe-failed, ${count('no-host-claim')} host-free`
  )
  for (const audit of audits) {
    const hosts = audit.hostsDeclared.length > 0 ? audit.hostsDeclared.join(', ') : '(no host declared)'
    lines.push(`    ${audit.verdict.toUpperCase()}  ${audit.file}  [${hosts}]`)
    if (audit.hostsNamedInReason.length > 0) {
      lines.push(`        reason names: ${audit.hostsNamedInReason.join(', ')}`)
    }
    for (const check of audit.checks) {
      lines.push(
        `        ${check.pass ? 'ok  ' : 'FAIL'} ${check.host} ${check.claim}: ` +
          `expected ${check.expected}, live "${check.actual}"`
      )
    }
    if (audit.checks.length === 0 && audit.verdict !== 'no-host-claim') {
      lines.push('        (no live check ran — see the violations above)')
    }
  }
  return lines
}

/** Human-readable report. Execution counts are printed FIRST, always. */
export function formatHeadContractReport(report: HeadContractReport): string {
  const lines: string[] = []
  lines.push(
    `[head-contract] scanned ${report.scanned} file(s); ` +
      `${report.applyHeadCalls} applyHead call(s), ${report.argumentFreeCalls} argument-free; ` +
      `${report.useSeoSlugs.length} static useSEO slug(s), ` +
      `${report.dynamicSlugs.length} dynamic (unchecked here — P1 judges those); ` +
      `${report.foreignWriters.length} foreign head writer(s) ` +
      `(${report.foreignWriters.filter((f) => f.exempted).length} declared); ` +
      `${report.additiveHeadCalls} additive JSON-LD useHead call(s)`
  )
  lines.push(
    `[head-contract] SPA coverage: ${report.coverage.asserting}/${report.coverage.views} view(s) ` +
      `re-assert the head; ${report.coverage.silent.length} silent`
  )
  if (report.scanned === 0) {
    lines.push('[head-contract] SCANNED NOTHING this run — this is a failure, not a pass.')
  }
  lines.push(...formatExemptionAudit(report))
  for (const v of report.violations) {
    lines.push(`  VIOLATION ${v.code} ${v.file}${v.line ? `:${v.line}` : ''} — ${v.detail}`)
  }
  for (const w of report.warnings) {
    lines.push(`  warn ${w.code} ${w.file} — ${w.detail}`)
  }
  if (report.violations.length === 0) {
    lines.push('[head-contract] PASS — .dcs/seo.yaml is the only writer of the managed head.')
  }
  return lines.join('\n')
}

/**
 * Assert the contract. `error` throws; `warn` prints; `off` is a no-op — except
 * that `scan-empty` and `no-seo-config` are NEVER downgraded, because "the rail
 * did not run" must not be silenceable by the mode that silences findings.
 */
export function assertHeadContract(
  input: HeadContractInput,
  mode: HeadContractMode = 'error',
  log: (msg: string) => void = console.warn
): HeadContractReport {
  const report = auditHeadContract(input)
  const notRun = report.violations.filter(
    (v) => v.code === 'scan-empty' || v.code === 'no-seo-config'
  )

  if (notRun.length > 0) {
    throw new HeadContractError(
      `${formatHeadContractReport(report)}\n\nThe head-contract audit did not run against ` +
        `anything it could judge. Fix the inputs before reading this as a pass.`,
      report
    )
  }

  if (mode === 'off') return report
  if (report.violations.length === 0) return report

  if (mode === 'warn') {
    log(formatHeadContractReport(report))
    log('[head-contract] in error mode the above would be RED.')
    return report
  }

  throw new HeadContractError(formatHeadContractReport(report), report)
}
